import { SharedModule } from './shared/shared.module';
import { NgModule, ErrorHandler, provideAppInitializer, inject } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withXsrfConfiguration
} from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';

import { AppComponent } from './app.component';
import { LogoutComponent } from './logout/logout.component';
import { CacheInterceptor } from 'Shared/app/core/interceptors/cache.interceptor';
import { FooterComponent } from './footer/footer.component';
import { AccessibilityStatementComponent } from './accessibility-statement/accessibility-statement.component';

import * as Sentry from '@sentry/angular';

import { WINDOW_PROVIDERS } from 'Shared/app/core/services/window.service';
import { RootStateModule } from 'Shared/app/root-state/root-state.module';
import { TranslateModule } from '@ngx-translate/core';

import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { StoreModule } from '@ngrx/store';
import { patientFeatureKey, patientReducer } from './state/patient.reducer';
import { environment } from 'environments/environment';
import { EffectsModule } from '@ngrx/effects';
import { PatientEffects } from './state/patient.effects';
import { StaticFormServiceModule } from 'Shared/app/static-form/services/static-form-services.module';
import { APP_BASE_HREF } from '@angular/common';
import { Router } from '@angular/router';
import { createErrorHandler } from 'Shared/app/core/chunk-loading-error-handler';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';

@NgModule({
  declarations: [AppComponent, FooterComponent, AccessibilityStatementComponent, LogoutComponent],
  bootstrap: [AppComponent],
  imports: [
    RootStateModule,
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    SharedModule,
    TranslateModule.forRoot({
      loader: provideTranslateHttpLoader({
        prefix: '/assets/locales/patient/',
        suffix: '.json'
      }),
      isolate: true,
      extend: true
    }),
    StoreModule.forFeature(patientFeatureKey, patientReducer),
    StoreDevtoolsModule.instrument({
      maxAge: 25, // Retains last 25 states
      logOnly: environment.production, // Restrict extension to log-only mode
      autoPause: true, // Pauses recording actions and state changes when the extension window is not open
      connectInZone: true
    }),
    EffectsModule.forFeature([PatientEffects]),
    StaticFormServiceModule
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CacheInterceptor,
      multi: true
    },
    {
      provide: ErrorHandler
    },
    WINDOW_PROVIDERS,
    {
      provide: APP_BASE_HREF,
      useValue: '/'
    },
    {
      provide: ErrorHandler,
      useValue: createErrorHandler()
    },
    {
      provide: Sentry.TraceService,
      deps: [Router]
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),

    { provide: APP_BASE_HREF, useValue: '/' },
    provideHttpClient(
      withInterceptorsFromDi(),
      withXsrfConfiguration({ cookieName: 'XSRF-TOKEN', headerName: 'X-XSRF-TOKEN' })
    )
  ]
})
export class AppModule { }